(() => {
  const ROOT_SELECTOR = '[data-living-glyph]';
  const instances = new WeakMap();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  class LivingGlyphRoutine {
    constructor(root) {
      this.root = root;
      this.story = root.querySelector('[data-living-glyph-story]');
      this.video = root.querySelector('[data-living-glyph-video]');
      this.chapters = Array.from(root.querySelectorAll('[data-living-glyph-chapter]'));
      this.progress = root.querySelector('[data-living-glyph-progress]');
      this.count = root.querySelector('[data-living-glyph-count]');
      this.statusText = root.querySelector('[data-living-glyph-status-text]');
      this.errorMessage = root.querySelector('[data-living-glyph-error]');
      this.language = root.dataset.language === 'ar' ? 'ar' : 'en';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.saveData = Boolean(navigator.connection && navigator.connection.saveData);
      this.duration = 0;
      this.targetTime = 0;
      this.playhead = 0;
      this.lastFrameTime = 0;
      this.frameId = 0;
      this.seekInFlight = false;
      this.isHydrated = false;
      this.isVisible = false;
      this.isDestroyed = false;
      this.activeIndex = -1;
      this.boundMeasure = this.measure.bind(this);
      this.boundTick = this.tick.bind(this);
      this.boundReducedMotion = this.handleMotionPreference.bind(this);
      this.initialize();
    }

    initialize() {
      if (!this.story || !this.video || this.chapters.length === 0) return;

      document.documentElement.classList.add('living-glyph-routine-document');
      this.setChapter(0);
      if (this.reducedMotion.matches || this.saveData) {
        this.enableStaticMode(this.saveData ? 'data-saver' : 'reduced-motion');
        return;
      }

      this.root.classList.add('is-motion-enabled');
      this.setStatus(this.language === 'ar' ? 'جارٍ تجهيز الرحلة' : 'Preparing the journey');
      this.addListeners();
      this.observe();
      this.measure();
    }

    addListeners() {
      window.addEventListener('scroll', this.boundMeasure, { passive: true });
      window.addEventListener('resize', this.boundMeasure, { passive: true });
      this.video.addEventListener('loadedmetadata', () => this.handleMetadata());
      this.video.addEventListener('canplay', () => this.markReady());
      this.video.addEventListener('seeked', () => this.handleSeeked());
      this.video.addEventListener('error', () => this.handleError());
      this.video.addEventListener('stalled', () => {
        if (!this.root.classList.contains('is-video-ready')) {
          this.setStatus(this.language === 'ar' ? 'الفيلم يحتاج لحظة إضافية' : 'The film needs another moment');
        }
      });

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', this.boundReducedMotion);
      } else if (typeof this.reducedMotion.addListener === 'function') {
        this.reducedMotion.addListener(this.boundReducedMotion);
      }
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.isVisible = true;
        this.hydrate();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.isVisible = Boolean(entry && entry.isIntersecting);
        if (entry && (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight * 1.5)) {
          this.hydrate();
        }
        if (this.isVisible) this.requestTick();
      }, { rootMargin: '140% 0px 140% 0px', threshold: 0 });
      this.observer.observe(this.story);
    }

    hydrate() {
      if (this.isHydrated || this.isDestroyed || this.root.classList.contains('is-static')) return;
      const source = this.video.dataset.src;
      if (!source) {
        this.handleError();
        return;
      }

      this.isHydrated = true;
      this.root.classList.add('is-loading');
      this.video.src = source;
      this.video.load();
    }

    handleMetadata() {
      const actualDuration = Number(this.video.duration);
      if (!Number.isFinite(actualDuration) || actualDuration <= 0) {
        this.handleError();
        return;
      }

      this.duration = actualDuration;
      this.video.pause();
      this.playhead = clamp(this.targetTime, 0, this.maximumTime());
      this.setStatus(this.language === 'ar' ? 'جاهز للسحب' : 'Ready to scroll');
      this.markReady();
      this.measure();
    }

    markReady() {
      if (!this.duration) return;
      this.root.classList.remove('is-loading');
      this.root.classList.add('is-video-ready');
    }

    handleSeeked() {
      this.seekInFlight = false;
      if (this.isDestroyed) return;
      this.requestTick();
    }

    handleError() {
      if (this.isDestroyed) return;
      this.seekInFlight = false;
      this.root.classList.remove('is-loading', 'is-video-ready', 'is-motion-enabled');
      this.root.classList.add('is-media-error');
      if (this.errorMessage) this.errorMessage.hidden = false;
      this.setAllChaptersReadable();
      this.stopTick();
    }

    handleMotionPreference(event) {
      if (event.matches) this.enableStaticMode('reduced-motion');
    }

    enableStaticMode(reason) {
      this.root.classList.remove('is-loading', 'is-video-ready', 'is-motion-enabled');
      this.root.classList.add('is-static');
      this.root.dataset.staticReason = reason;
      this.setAllChaptersReadable();
      this.stopTick();
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
      }
    }

    setAllChaptersReadable() {
      this.chapters.forEach((chapter) => {
        chapter.classList.add('is-active');
        chapter.classList.remove('is-before', 'is-after');
        chapter.removeAttribute('aria-hidden');
        chapter.querySelectorAll('a, button').forEach((control) => control.removeAttribute('tabindex'));
      });
    }

    measure() {
      if (this.isDestroyed || this.root.classList.contains('is-static') || this.root.classList.contains('is-media-error')) return;
      const rect = this.story.getBoundingClientRect();
      const scrollableDistance = Math.max(1, this.story.offsetHeight - window.innerHeight);
      const progress = clamp(-rect.top / scrollableDistance);

      this.root.style.setProperty('--lg-progress', progress.toFixed(5));
      this.targetTime = this.duration ? progress * this.maximumTime() : 0;
      this.updateChapter(progress);
      this.requestTick();
    }

    maximumTime() {
      if (!this.duration) return 0;
      return Math.max(0, this.duration - Math.min(1 / 60, this.duration * 0.002));
    }

    updateChapter(progress) {
      let nextIndex = this.chapters.length - 1;
      for (let index = 0; index < this.chapters.length; index += 1) {
        const chapter = this.chapters[index];
        const start = Number(chapter.dataset.start || 0);
        const end = Number(chapter.dataset.end || 1);
        if (progress >= start && (progress < end || index === this.chapters.length - 1)) {
          nextIndex = index;
          break;
        }
      }
      this.setChapter(nextIndex);
    }

    setChapter(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      this.chapters.forEach((chapter, chapterIndex) => {
        const isActive = chapterIndex === index;
        chapter.classList.toggle('is-active', isActive);
        chapter.classList.toggle('is-before', chapterIndex < index);
        chapter.classList.toggle('is-after', chapterIndex > index);
        chapter.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        chapter.querySelectorAll('a, button').forEach((control) => {
          if (isActive) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', '-1');
        });
      });
      if (this.count) this.count.textContent = String(index + 1).padStart(2, '0');
      this.root.dataset.activeChapter = String(index + 1);
    }

    requestTick() {
      if (this.frameId || this.isDestroyed || !this.duration || this.root.classList.contains('is-static') || this.root.classList.contains('is-media-error')) return;
      this.frameId = window.requestAnimationFrame(this.boundTick);
    }

    stopTick() {
      if (this.frameId) window.cancelAnimationFrame(this.frameId);
      this.frameId = 0;
      this.lastFrameTime = 0;
    }

    tick(timestamp) {
      this.frameId = 0;
      if (this.isDestroyed || !this.duration) return;

      const elapsed = this.lastFrameTime ? Math.min(64, timestamp - this.lastFrameTime) : 16.67;
      this.lastFrameTime = timestamp;
      const easing = 1 - Math.exp(-0.012 * elapsed);
      const difference = this.targetTime - this.playhead;

      if (Math.abs(difference) < 0.0015) this.playhead = this.targetTime;
      else this.playhead += difference * easing;

      this.seekLatestPlayhead();

      const needsMoreEasing = Math.abs(this.targetTime - this.playhead) >= 0.0015;
      const seekNeedsCatchup = Math.abs(this.video.currentTime - this.targetTime) > 0.035;
      if (needsMoreEasing || this.seekInFlight || seekNeedsCatchup) this.requestTick();
      else this.lastFrameTime = 0;
    }

    seekLatestPlayhead() {
      if (this.seekInFlight || this.video.readyState < 1) return;
      const nextTime = clamp(this.playhead, 0, this.maximumTime());
      if (Math.abs(this.video.currentTime - nextTime) < 1 / 90) return;

      this.seekInFlight = true;
      try {
        this.video.pause();
        this.video.currentTime = nextTime;
      } catch (error) {
        this.seekInFlight = false;
        if (error && error.name !== 'InvalidStateError') this.handleError();
      }
    }

    setStatus(message) {
      if (this.statusText) this.statusText.textContent = message;
    }

    destroy() {
      this.isDestroyed = true;
      document.documentElement.classList.remove('living-glyph-routine-document');
      this.stopTick();
      window.removeEventListener('scroll', this.boundMeasure);
      window.removeEventListener('resize', this.boundMeasure);
      if (this.observer) this.observer.disconnect();
      if (typeof this.reducedMotion.removeEventListener === 'function') {
        this.reducedMotion.removeEventListener('change', this.boundReducedMotion);
      } else if (typeof this.reducedMotion.removeListener === 'function') {
        this.reducedMotion.removeListener(this.boundReducedMotion);
      }
      this.video.pause();
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new LivingGlyphRoutine(root);
      instances.set(root, instance);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;
    if (instance) instance.destroy();
  });
})();
